<template>
  <!--
    PAINEL, nao modal (decisao do Ariel, 15/09).

    Como modal, isto tapava o atendimento inteiro justamente quando o
    profissional precisa olhar para o prontuario que a proposta vai alterar — e
    "aprovar sem ver onde cai" e o oposto do que a validacao humana existe para
    garantir. Aqui as propostas ficam ao lado das notas, na mesma pagina em que
    o dado vai parar.
  -->
  <v-card
    v-if="temAlgoParaMostrar"
    outlined
    class="ds-section-card mb-4"
  >
    <v-card-title class="ds-section-card-title">
      <v-icon small color="primary" class="mr-2">mdi-microphone-message</v-icon>
      <span>Comandos ditos na consulta</span>
      <v-spacer />
      <v-btn
        v-if="!processando"
        small
        color="primary"
        :disabled="!transcricao || !transcricao.trim()"
        @click="processar"
      >
        <v-icon small left>mdi-auto-fix</v-icon>
        {{ jaProcessou ? "Ler de novo" : "Preparar propostas" }}
      </v-btn>
    </v-card-title>
    <v-divider></v-divider>

    <v-card-text class="pa-3">
      <p class="cv-lead">
        A salvIA le a conversa inteira, procura o que voce ditou e prepara as
        propostas. <strong>Nada e gravado no prontuario</strong> antes de voce
        aprovar.
      </p>

      <!-- O que foi ouvido durante a consulta. Não é o que gera as propostas —
           serve para o profissional conferir que o sistema escutou. -->
      <div v-if="comandos.length" class="cv-ouvidos">
        <div class="cv-ouvidos-titulo">Comandos que ouvi na consulta:</div>
        <v-chip
          v-for="(c, i) in comandos"
          :key="i"
          x-small
          outlined
          class="mr-1 mb-1"
        >
          {{ c.comando }}
        </v-chip>
      </div>

      <div v-if="processando" class="cv-estado">
        <v-progress-circular indeterminate size="18" width="2" color="primary" />
        <span class="ml-2">Lendo a consulta e preparando as propostas…</span>
      </div>

      <div v-else-if="erro" class="cv-estado cv-erro">
        <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
        {{ erro }}
        <v-btn text x-small color="primary" @click="processar">Tentar de novo</v-btn>
      </div>

      <template v-else-if="jaProcessou">
        <ProposalCard
          v-for="p in propostas"
          :key="p.id"
          :proposal="p"
          @update="aoAtualizarProposta"
        />

        <div v-if="!propostas.length && !naoCriados.length" class="cv-estado cv-sem-proposta">
          <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
          A salvIA nao encontrou nada para registrar nesta consulta.
        </div>

        <!--
          Comando reconhecido que NÃO virou proposta (erro ao criar). Mostrar é
          obrigatório: senão o profissional vê menos cards do que ditou e não
          tem como saber o que ficou de fora.
        -->
        <div v-if="naoCriados.length" class="cv-nao-criados">
          <div class="cv-ouvidos-titulo">
            <v-icon x-small color="warning" class="mr-1">mdi-alert-outline</v-icon>
            Nao consegui preparar proposta para:
          </div>
          <ul class="cv-lista-erro">
            <li v-for="(n, i) in naoCriados" :key="i">
              {{ n.comando || "(comando sem texto)" }}
              <span class="cv-erro-detalhe">— {{ n.erro }}</span>
            </li>
          </ul>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { comandosDaConsulta } from "@/services/assistantService";
import ProposalCard from "@/features/assistant/ProposalCard.vue";

/**
 * salvia-861/862: os comandos ditos na consulta viram propostas que o médico
 * aprova ou recusa.
 *
 * ⚠️ ESTE PAINEL VIROU A REDE, NÃO O CAMINHO PRINCIPAL (decisão do Ariel, 22/09).
 *
 * Até 21/09 aqui era o único gerador: uma passada, no fim, com a transcrição
 * inteira, disparada por clique. O clique saiu. A proposta agora nasce NO
 * MOMENTO do comando, aparece nas Notas Salvas
 * ({@link AppointmentDialog}.prepareInstantProposal), e este painel passou a
 * existir para VARRER o que a captura ao vivo não pegou — o comando que o
 * detector classificou como "não sei" e o que falhou na hora.
 *
 * O motivo de mandar a transcrição inteira continua igual, e é o que define a
 * funcionalidade: o comando quase nunca carrega a informação. "Anota o que ela
 * falou sobre o sono" só faz sentido para quem tem a conversa toda. Os dois
 * caminhos mandam a conversa; o imediato manda também `comando_alvo`, para o
 * agente resolver só aquele comando.
 *
 * A DUPLICATA que a versão anterior deste comentário temia ("dois geradores
 * criariam proposta duplicada para o mesmo comando") é real e foi resolvida onde
 * ela acontece, e não evitando o segundo gerador: o Laravel deduplica por
 * PAYLOAD na criação (ProposalController, origin `voz_consulta`, janela de 12h).
 * Por payload e não por texto — casar o texto do agente com o do detector seria
 * chute, pelo mesmo motivo descrito em `limparSeTudoVirouProposta`.
 *
 * Os chips que aparecem durante a consulta (salvia-860) continuam valendo, mas
 * como RETORNO VISUAL: dizem "ouvi você", e nada mais.
 *
 * Não há mecanismo novo aqui. O assistente já tem ferramentas que CRIAM proposta
 * (propor_criar_consulta, propor_solicitar_anamnese, propor_mover_consulta...) e
 * o {@link ProposalCard} já sabe aprovar, recusar, editar e pedir a confirmação
 * extra de ação sensível (Tier 2). O que faltava era o comando falado chegar até
 * lá — este painel é essa ponte.
 *
 * POR QUE PROPOSTA, E NÃO GRAVAÇÃO DIRETA: é o requisito literal da salvia-840
 * ("SEMPRE COM VALIDAÇÃO HUMANA antes de persistir"), e resolve de quebra o
 * falso positivo do gatilho — "Salvia" é o nome do produto, e se o profissional
 * disser "o Salvia travou" no começo de uma frase, a proposta correspondente é
 * simplesmente recusada e nada acontece.
 *
 * UM COMANDO POR VEZ, sem histórico compartilhado: cada comando é uma ordem
 * independente. Passar os anteriores como contexto faria o agente encadear
 * ("agende o retorno" + "peça o hemograma" viraria uma coisa só) e misturaria
 * comandos que o médico deu em momentos diferentes da consulta.
 */
export default {
  name: "ComandosDeVozPainel",

  components: { ProposalCard },

  props: {
    /** Só para mostrar o que foi ouvido — não é o que gera as propostas. */
    comandos: { type: Array, default: () => [] },
    /** É DAQUI que as propostas saem: a conversa inteira. */
    transcricao: { type: String, default: "" },
    patientId: { type: [Number, String], default: null },
  },

  data() {
    return {
      propostas: [],
      naoCriados: [],
      processando: false,
      erro: "",
      // Distingue "ainda nao pedi" de "pedi e nao veio nada". Sem isto, o painel
      // anunciaria "nao encontrei nada" antes mesmo de ter lido a consulta.
      jaProcessou: false,
    };
  },

  computed: {
    /**
     * O painel aparece quando ha comando ouvido OU resultado na tela.
     *
     * A segunda metade importa: ao processar tudo com sucesso a lista de
     * comandos e esvaziada (eles viraram proposta e agora vivem no Laravel), e
     * sem ela o painel sumiria levando junto os cards que o medico ainda
     * precisa aprovar.
     */
    temAlgoParaMostrar() {
      return (
        this.comandos.length > 0 ||
        this.propostas.length > 0 ||
        this.naoCriados.length > 0
      );
    },
  },

  methods: {
    async processar() {
      this.erro = "";
      this.propostas = [];
      this.naoCriados = [];

      if (!this.transcricao || !this.transcricao.trim()) {
        this.erro =
          "A consulta ainda não tem transcrição — sem ela não dá para saber a " +
          "que os comandos se referem.";
        return;
      }

      this.processando = true;
      this.erro = "";
      try {
        const r = await comandosDaConsulta(this.transcricao, this.patientId);
        this.propostas = r.proposals || [];
        this.naoCriados = r.naoCriados || [];
        this.jaProcessou = true;
        this.limparSeTudoVirouProposta();
      } catch (e) {
        this.erro =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Não foi possível ler os comandos agora.";
      } finally {
        this.processando = false;
      }
    },

    /**
     * Os comandos so somem da lista do atendimento se TODOS viraram proposta. Proposta vive no Laravel, na fila do medico — mante-los aqui
     * depois disso faria a guarda de fechamento cobrar revisao de coisa ja
     * revisada, e aviso que repete sem motivo e aviso que se aprende a ignorar.
     * (Como painel, isto roda ao terminar de processar, nao ao fechar.)
     *
     * ⚠️ Mas se QUALQUER UM falhou, nada e limpo — nem os que deram certo.
     *
     * A versao anterior olhava so o `erro` (a falha da chamada inteira) e
     * limpava tudo quando um comando individual falhava em `naoCriados`. Em
     * stage, em 15/09, foi isto que aconteceu: o POST /api/proposals voltou 422,
     * o comando apareceu como "nao consegui preparar proposta" e, ao fechar,
     * sumia para sempre — o medico ditou, o sistema confirmou que ouviu, e
     * depois perdeu.
     *
     * Nao da para devolver so o que falhou: o texto em `naoCriados` vem do
     * agente ("Salvia Anote o que...") e o da lista vem do detector do front
     * ("Anote o que..."), entao casar um com o outro seria chute. Diante da
     * duvida, guardar demais custa um clique; guardar de menos custa a consulta.
     */
    limparSeTudoVirouProposta() {
      if (this.erro || this.naoCriados.length) return;
      this.$emit("revisados", []);
    },

    aoAtualizarProposta(atualizada) {
      this.propostas = this.propostas.map((p) =>
        p.id === atualizada.id ? atualizada : p,
      );
    },
  },
};
</script>

<style scoped>
.cv-ouvidos {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 8px 10px;
  margin-bottom: 16px;
}
.cv-ouvidos-titulo {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 6px;
}
.cv-nao-criados {
  margin-top: 16px;
}
.cv-lista-erro {
  font-size: 0.84rem;
  padding-left: 18px;
  margin: 0;
}
.cv-erro-detalhe {
  color: rgba(0, 0, 0, 0.5);
}

.cv-lead {
  color: rgba(0, 0, 0, 0.7);
  font-size: 0.92rem;
  margin-bottom: 16px;
}
.cv-item {
  border-left: 3px solid var(--ds-primary, #1f4d3f);
  padding-left: 12px;
  margin-bottom: 20px;
}
.cv-comando {
  font-weight: 600;
  font-size: 0.94rem;
  margin-bottom: 8px;
}
.cv-estado {
  display: flex;
  align-items: center;
  font-size: 0.84rem;
  color: rgba(0, 0, 0, 0.6);
}
.cv-erro {
  color: var(--ds-danger-mid, #b00020);
}
.cv-sem-proposta {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 8px 10px;
}
</style>
